import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import { Button, CircularProgress, TableCell, TableRow } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";
import RecordFields from "../layout/RecordFields";
import { brandLabel, useBrandNames } from "./brands";
import {
  DataTable,
  PageHeader,
  SectionHeader,
  StatusChip,
  errorMessage,
  fmtDateTime,
  gbp,
  useCloStyles,
} from "./shared";

const REFUND_COLUMNS = [
  { label: "ID" },
  { label: "Transaction at" },
  { label: "Event" },
  { label: "Status" },
  { label: "Reason" },
  { label: "ARN" },
  { label: "Amount", numeric: true },
];

const CANDIDATE_COLUMNS = [
  { label: "ID" },
  { label: "Transaction at" },
  { label: "Merchant" },
  { label: "Amount", numeric: true },
  { label: "" },
];

const fieldsOf = (t: any, brand: string): { label: string; value: any }[] => [
  { label: "Network event", value: t.network_event_id },
  { label: "Network", value: t.network },
  { label: "Network txn id", value: t.network_transaction_id },
  { label: "Event type", value: t.event_type },
  { label: "Status", value: t.status },
  { label: "Reason", value: t.reason },
  { label: "Amount", value: gbp(t.amount_pence) },
  { label: "Currency", value: t.currency },
  { label: "Transaction at", value: t.transaction_at ? fmtDateTime(t.transaction_at) : null },
  { label: "Cleared at", value: t.cleared_at ? fmtDateTime(t.cleared_at) : null },
  { label: "ARN", value: t.arn },
  { label: "Original ARN", value: t.original_arn },
  { label: "Original txn id", value: t.original_transaction_id },
  { label: "Merchant", value: t.merchant_name },
  { label: "MID", value: t.mid },
  { label: "Network merchant id", value: t.network_merchant_id },
  { label: "Network store id", value: t.network_store_id },
  { label: "Acquirer BIN", value: t.acquirer_bin },
  { label: "Offer id", value: t.offer_id },
  { label: "Offer event id", value: t.offer_event_id },
  { label: "Merchant external id", value: t.merchant_external_id },
  { label: "Visa card id", value: t.network_card_id },
  { label: "Purchase date only", value: t.purchase_date_only ? "Yes" : null },
  { label: "User key", value: t.user_key },
  { label: "Card last 4", value: t.card_last4 },
  { label: "User ID", value: t.user_id },
  { label: "Card id", value: t.card_id },
  { label: "Brand", value: t.brand_id != null ? brand : null },
  { label: "Campaign id", value: t.campaign_id },
  { label: "Matched at", value: t.matched_at ? fmtDateTime(t.matched_at) : null },
  { label: "Created", value: t.createdAt ? fmtDateTime(t.createdAt) : null },
  { label: "Updated", value: t.updatedAt ? fmtDateTime(t.updatedAt) : null },
];

const TransactionShow = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [txn, setTxn] = useState<any>(null);
  const [linking, setLinking] = useState(false);
  const names = useBrandNames([txn?.brand_id]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res: any = await axios.get(`${ENDPOINTS.CLO_TRANSACTIONS}/${id}`, getConfigGet({}));
        setTxn(res.data);
      } catch (err) {
        notify(errorMessage(err, "Failed to load transaction"), "warning");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const link = async (settlementId: number) => {
    if (!window.confirm(`Link refund #${id} to settlement #${settlementId}? The reward engine then applies the refund to that purchase.`)) return;
    try {
      setLinking(true);
      const res: any = await axios.patch(
        `${ENDPOINTS.CLO_TRANSACTIONS}/${id}`,
        { originalTransactionId: settlementId },
        { headers: getConfigPatch() }
      );
      setTxn(res.data);
      notify(`Refund linked to settlement #${settlementId}`, "info");
    } catch (err) {
      notify(errorMessage(err, "Failed to link the refund"), "warning");
    } finally {
      setLinking(false);
    }
  };

  const back = (
    <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/clo/transactions")}>Transactions</Button>
  );

  if (loading) return <div className={classes.loadingContainer}><CircularProgress size={40} /></div>;
  if (!txn) {
    return (
      <div className={classes.main}>
        <PageHeader title="Transaction not found" actions={back} />
        <Notification />
      </div>
    );
  }

  const fields = fieldsOf(txn, brandLabel(names, txn.brand_id));
  const refunds: any[] = txn.refunds || [];
  const candidates: any[] = txn.candidateSettlements || [];

  return (
    <div className={classes.main}>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            Transaction #{txn.id}
            <StatusChip status={txn.status} />
          </span>
        }
        subtitle={[txn.event_type, txn.merchant_name, gbp(txn.amount_pence), fmtDateTime(txn.transaction_at)]
          .filter(Boolean)
          .join(" · ")}
        actions={back}
      />

      <RecordFields labels={fields.map((f) => f.label)} values={fields.map((f) => f.value)} />

      {txn.reason === "ambiguous_refund" && (
        <>
          <SectionHeader
            title="Which shipment is this refund for?"
            meta="Several settlements share this purchase's VipTransactionId"
          />
          <DataTable columns={CANDIDATE_COLUMNS} isEmpty={candidates.length === 0} empty="No matched settlement to link to yet.">
            {candidates.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.id}</TableCell>
                <TableCell>{fmtDateTime(c.transaction_at)}</TableCell>
                <TableCell>{c.merchant_name || "—"}</TableCell>
                <TableCell align="right">{gbp(c.amount_pence)}</TableCell>
                <TableCell align="right">
                  <Button size="small" color="primary" variant="outlined" disabled={linking} onClick={() => link(c.id)}>
                    Link
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </DataTable>
        </>
      )}

      <SectionHeader
        title="Refunds"
        meta={`Refunded ${gbp(txn.refundedPence)} · Net kept ${gbp(txn.netKeptPence)}`}
      />

      <DataTable columns={REFUND_COLUMNS} isEmpty={refunds.length === 0} empty="No refunds against this transaction.">
        {refunds.map((r) => (
          <TableRow
            key={r.id}
            hover
            className={classes.clickableRow}
            onClick={() => history.push(`/clo/transactions/${r.id}`)}
          >
            <TableCell>{r.id}</TableCell>
            <TableCell>{fmtDateTime(r.transaction_at)}</TableCell>
            <TableCell>{r.event_type}</TableCell>
            <TableCell><StatusChip status={r.status} /></TableCell>
            <TableCell>{r.reason || "—"}</TableCell>
            <TableCell>{r.arn || "—"}</TableCell>
            <TableCell align="right">{gbp(r.amount_pence)}</TableCell>
          </TableRow>
        ))}
      </DataTable>
      <Notification />
    </div>
  );
};

export default TransactionShow;
